// System dependencies

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Application dependencies

import { RootState } from '@/redux/store';
import { AuthChallenge, AuthFields, AuthProviders, AuthRedux, AuthStatus, AuthUser } from '@/_auth/interfaces/auth.interfaces';
import { AuthError } from '@/_auth/lib/auth.types';

// State

const initialState: AuthRedux = {
  [AuthFields.Challenge]: null,
  [AuthFields.DisplayWarning]: false,
  [AuthFields.Error]: null,
  [AuthFields.HasVerifiedFactor]: false,
  [AuthFields.Provider]: null,
  [AuthFields.Status]: AuthStatus.Unauthenticated,
  [AuthFields.User]: null,
  [AuthFields.UserTotpWarning]: false,
};

// Slices

const AuthSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    authReset: () => initialState,
    authResetSession: (state) => {
      state[AuthFields.Challenge] = null;
      state[AuthFields.DisplayWarning] = false;
      state[AuthFields.Error] = null;
      state[AuthFields.HasVerifiedFactor] = false;
      state[AuthFields.Provider] = null;
      state[AuthFields.Status] = AuthStatus.Unauthenticated;
    },
    authResetUser: (state) => {
      state[AuthFields.UserTotpWarning] = false;
      state[AuthFields.User] = null;
    },
    authSetChallenge: (state, action: PayloadAction<AuthChallenge | null>) => {
      state[AuthFields.Challenge] = action.payload;
    },
    authSetDisplayWarning: (state, action: PayloadAction<boolean>) => {
      state[AuthFields.DisplayWarning] = action.payload;
    },
    authSetError: (state, action: PayloadAction<AuthError | string | null>) => {
      state[AuthFields.Error] = action.payload;
    },
    authSetHasVerifiedFactor: (state, action: PayloadAction<boolean>) => {
      state[AuthFields.HasVerifiedFactor] = action.payload;
    },
    authSetProvider: (state, action: PayloadAction<AuthProviders | null>) => {
      state[AuthFields.Provider] = action.payload;
    },
    authSetStatus: (state, action: PayloadAction<AuthStatus>) => {
      state[AuthFields.Status] = action.payload;
    },
    authSetUser: (state, action: PayloadAction<AuthUser | null>) => {
      state[AuthFields.User] = action.payload;
    },
    authSetUserTotpWarning: (state, action: PayloadAction<boolean>) => {
      state[AuthFields.UserTotpWarning] = action.payload;
    },
  },
});

// Exports

export const { authReset, authResetSession, authResetUser, authSetChallenge, authSetDisplayWarning, authSetError, authSetHasVerifiedFactor, authSetProvider, authSetStatus, authSetUser, authSetUserTotpWarning } = AuthSlice.actions;

export const getAuthSelector = (state: RootState) => state.auth;

export default AuthSlice.reducer;
